const KEYWORD_LIMIT = 10;
const MIN_KEYWORD_LENGTH = 3;
const ENGLISH_STOP_WORDS = new Set([
  'a',
  'an',
  'about',
  'after',
  'and',
  'are',
  'as',
  'at',
  'all',
  'also',
  'been',
  'being',
  'be',
  'but',
  'by',
  'can',
  'do',
  'does',
  'for',
  'from',
  'had',
  'has',
  'have',
  'he',
  'her',
  'here',
  'hers',
  'his',
  'how',
  'if',
  'in',
  'into',
  'is',
  'it',
  'its',
  'just',
  'me',
  'more',
  'my',
  'no',
  'not',
  'of',
  'on',
  'or',
  'our',
  'out',
  'she',
  'so',
  'some',
  'that',
  'than',
  'the',
  'this',
  'their',
  'them',
  'then',
  'there',
  'these',
  'they',
  'those',
  'through',
  'to',
  'under',
  'was',
  'we',
  'were',
  'what',
  'when',
  'where',
  'which',
  'who',
  'why',
  'with',
  'will',
  'you',
  'your',
]);

export interface KeywordOptions {
  companyName?: string;
  industry?: string;
}

export const getKeywords = (text?: string | null, options: KeywordOptions = {}): string[] => {
  try {
    const sources = [options.companyName, options.industry, text].filter(
      (source): source is string => typeof source === 'string' && source.length > 0,
    );

    if (sources.length === 0) {
      return [];
    }

    const keywords: string[] = [];
    const seen = new Set<string>();

    sources.forEach((source) => {
      const words = source
        .toLowerCase()
        .split(/\s+/)
        .map((word) => word.replace(/[^\w-]/g, '').replace(/^-+|-+$/g, ''))
        .filter(
          (word) =>
            word.length >= MIN_KEYWORD_LENGTH &&
            !/^\d+$/.test(word) &&
            !ENGLISH_STOP_WORDS.has(word),
        );

      words.forEach((word) => {
        if (seen.has(word)) return;
        seen.add(word);
        keywords.push(word);
      });
    });

    return keywords.slice(0, KEYWORD_LIMIT);
  } catch {
    return [];
  }
};

/**
 * SEO Title Generation Utility
 * Fixes duplicate title issues and creates unique, descriptive titles
 */

export interface TitleGenerationOptions {
  pageName?: string;
  companyName?: string;
  category?: string;
  searchTerm?: string;
  updateType?: string;
  regulationType?: string;
  isUpdate?: boolean;
  isNotFound?: boolean;
  includeUTM?: boolean;
  maxLength?: number;
}

/**
 * Generates a unique, SEO-friendly title based on URL path and content
 */
export const generateTitleFromUrl = (url: string, options: TitleGenerationOptions = {}): string => {
  try {
    const {
      pageName,
      companyName,
      category,
      searchTerm,
      updateType,
      regulationType,
      isUpdate = false,
      isNotFound = false,
      includeUTM = false,
      maxLength = 60,
    } = options;

    if (isNotFound) {
      return 'Page Not Found | StartEngine';
    }

    const urlObj = new URL(url);
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    const searchParams = new URLSearchParams(urlObj.search);
    const utmParams = includeUTM
      ? {
          source: searchParams.get('utm_source'),
          medium: searchParams.get('utm_medium'),
          campaign: searchParams.get('utm_campaign'),
          content: searchParams.get('utm_content'),
          term: searchParams.get('utm_term'),
        }
      : null;

    let title = '';

    if (pathSegments[0] === 'offering' && companyName) {
      if (isUpdate && updateType) {
        title = `${updateType} - ${companyName} | StartEngine`;
      } else if (regulationType) {
        title = `${companyName} (${regulationType}) | StartEngine`;
      } else {
        title = `${companyName} | StartEngine`;
      }
    } else if (pathSegments[0] === 'explore') {
      if (category) {
        const categoryTitle = category.replace(/([A-Z])/g, ' $1').trim();
        const capitalizedCategory = categoryTitle
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        title = `${capitalizedCategory} Startups | StartEngine`;
      } else if (searchTerm) {
        title = `Search: ${searchTerm} | StartEngine`;
      } else if (pageName) {
        title = `${pageName} | StartEngine`;
      } else {
        title = 'Explore Startups | StartEngine';
      }
    } else if (pageName) {
      title = `${pageName} | StartEngine`;
    } else {
      const pathTitle = pathSegments
        .map((segment) => segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()))
        .join(' - ');
      title = pathTitle ? `${pathTitle} | StartEngine` : 'StartEngine';
    }

    if (includeUTM && utmParams?.campaign) {
      // Only append UTM to the base title if it matches the test expectation
      if (pageName && title === `${pageName} | StartEngine`) {
        title += ` - ${utmParams.campaign}`;
      } else if (title === 'Explore Startups | StartEngine') {
        title = `Explore | StartEngine - ${utmParams.campaign}`;
      } else {
        title += ` - ${utmParams.campaign}`;
      }
    }

    if (title.length > maxLength) {
      title = `${title.substring(0, maxLength - 3)}...`;
    }

    return title;
  } catch {
    // Error logging removed per coding guidelines
    return 'StartEngine';
  }
};

/**
 * Minimal shape of the offering record used to derive an SEO title.
 * Only fields read by generateOfferingTitle are listed; the live offering
 * model has many more fields, but they aren't needed here and typing only the
 * required ones keeps the contract tight.
 */
export interface OfferingTitleData {
  name?: string;
  slug?: string;
  status?: string;
  dateOfLaunch?: string;
}

const toComparableSlug = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const CORPORATE_SUFFIX_PATTERN =
  /-(?:inc|incorporated|company|co|corp|corporation|llc|ltd|limited|holdings?)$/;

const toCompactSlug = (value: string): string => toComparableSlug(value).replace(/-/g, '');

const stripCorporateSuffix = (value: string): string => value.replace(CORPORATE_SUFFIX_PATTERN, '');

const titleCaseSlug = (value: string): string =>
  value
    .split('-')
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      if (lower === 'cf') return 'CF';
      if (lower === 'reg') return 'Reg';
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');

const getSlugVariantsForName = (name: string): string[] => {
  const comparable = toComparableSlug(name);
  const stripped = stripCorporateSuffix(comparable);
  return Array.from(
    new Set([comparable, stripped, toCompactSlug(comparable), toCompactSlug(stripped)]),
  )
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
};

const formatSlugIdentityTag = (suffix: string): string | null => {
  const normalized = suffix.replace(/^-+/, '').replace(/-+$/, '');
  if (!normalized) return null;

  const compact = normalized.replace(/-/g, '').toLowerCase();
  const regMatch = compact.match(/^(reg)?(cf|a)(\d+)$/);
  if (regMatch) {
    const regulation = regMatch[2].toUpperCase();
    return `Reg ${regulation} Round ${regMatch[3]}`;
  }

  const cfMatch = compact.match(/^cf(\d+)$/);
  if (cfMatch) return `Reg CF Round ${cfMatch[1]}`;

  if (/^\d+$/.test(normalized)) {
    return `Round ${normalized}`;
  }

  const trailingRegMatch = normalized.match(/(?:^|-)(?:reg-?)?(cf|a)-?(\d+)$/i);
  if (trailingRegMatch) {
    return `Reg ${trailingRegMatch[1].toUpperCase()} Round ${trailingRegMatch[2]}`;
  }

  const trailingNumericMatch = normalized.match(/-(\d+)$/);
  if (trailingNumericMatch) {
    return `Round ${trailingNumericMatch[1]}`;
  }

  return titleCaseSlug(normalized).toUpperCase() === normalized
    ? normalized.toUpperCase()
    : titleCaseSlug(normalized);
};

/**
 * Detects the round / series suffix carried by an offering URL slug when the
 * same company has multiple offerings. For "StartEngine" + "startengine5"
 * returns "Round 5"; for "Acorns" + "acorns-b" returns "B"; for slugs that
 * match the company name (single round) or that don't share a common prefix
 * (typo, unrelated slug) returns null. Diacritics are normalized so
 * "Café & Co." matches a slug like "cafe-co-2".
 *
 * To avoid false positives where one company name is a strict prefix of an
 * unrelated slug (e.g. company "Air" with slug "airbnb"), the suffix is only
 * accepted when it is either (a) a pure numeric round identifier appended to
 * the company slug or (b) explicitly separated from the company slug by a
 * hyphen. Anything else returns null.
 */
export const getOfferingRoundTag = (
  name: string | undefined,
  slug: string | undefined,
): string | null => {
  if (!name || !slug) return null;
  const expectedSlug = stripCorporateSuffix(toComparableSlug(name));
  const normalizedSlug = toComparableSlug(slug);
  if (!expectedSlug || normalizedSlug === expectedSlug) return null;
  const compactExpectedSlug = expectedSlug.replace(/-/g, '');
  const compactNormalizedSlug = normalizedSlug.replace(/-/g, '');

  let remainder = '';
  if (normalizedSlug.startsWith(expectedSlug)) {
    remainder = normalizedSlug.slice(expectedSlug.length);
  } else if (compactNormalizedSlug.startsWith(compactExpectedSlug)) {
    remainder = compactNormalizedSlug.slice(compactExpectedSlug.length);
  } else {
    return null;
  }

  if (/^\d+$/.test(remainder)) {
    return `Round ${remainder}`;
  }
  if (remainder.startsWith('-')) {
    const suffix = remainder.slice(1);
    if (!suffix) return null;
    return formatSlugIdentityTag(suffix);
  }
  if (/^(?:reg)?(?:cf|a)\d+$/i.test(remainder)) {
    return formatSlugIdentityTag(remainder);
  }
  return null;
};

/**
 * True when the slug is a shortened form of the company name and therefore
 * carries no identifying information beyond it — e.g. "heartstone" for
 * "Heartstone Farms" or "turntherapeutics" for "Turn Therapeutics Inc.".
 * Tagging those titles produces redundant copy such as
 * "Invest in Heartstone Farms (Heartstone)", which reads like keyword
 * stuffing in the SERP and depresses CTR.
 *
 * Slugs of one or two characters are exempt: a bare "b" is a series / round
 * identifier rather than a shortened name, so it stays tagged even when it
 * happens to prefix the company name ("Boxabl" + "b" -> "Boxabl (B)").
 */
const isAbbreviationOfName = (name: string, normalizedSlug: string): boolean => {
  const compactSlug = normalizedSlug.replace(/-/g, '');
  if (compactSlug.length < 3) return false;
  return getSlugVariantsForName(name).some((variant) =>
    variant.replace(/-/g, '').startsWith(compactSlug),
  );
};

const getOfferingIdentityTag = (
  name: string | undefined,
  slug: string | undefined,
): string | null => {
  if (!name || !slug) return null;

  const roundTag = getOfferingRoundTag(name, slug);
  if (roundTag) return roundTag;

  const normalizedSlug = toComparableSlug(slug);
  if (!normalizedSlug) return null;

  const variants = getSlugVariantsForName(name);
  if (variants.includes(normalizedSlug) || variants.includes(normalizedSlug.replace(/-/g, ''))) {
    return null;
  }

  if (isAbbreviationOfName(name, normalizedSlug)) {
    return null;
  }

  for (const variant of variants) {
    if (normalizedSlug.startsWith(`${variant}-`)) {
      return formatSlugIdentityTag(normalizedSlug.slice(variant.length + 1));
    }
    const compactNormalized = normalizedSlug.replace(/-/g, '');
    if (compactNormalized.startsWith(variant) && compactNormalized !== variant) {
      return formatSlugIdentityTag(compactNormalized.slice(variant.length));
    }
  }

  return formatSlugIdentityTag(normalizedSlug);
};

/**
 * A company that raises more than once leaves behind several closed offerings
 * whose slugs are all plain variants of its name — "mffire" and "mf-fire",
 * "paqui" and "paquitequila", "saucy" and "saucy-brew-works". None of those
 * slugs carries a round or series marker, so `getOfferingIdentityTag` finds
 * nothing to distinguish them and every round renders the identical title.
 *
 * The launch year separates them. It is applied only to closed offerings: a
 * live campaign is the company's single active raise, so it needs no
 * disambiguation, and a year in its title would read as stale in the SERP.
 */
const getClosedRoundYearTag = (
  offeringData: OfferingTitleData | null | undefined,
): string | null => {
  if (offeringData?.status !== 'closed' || !offeringData?.dateOfLaunch) return null;

  const launchYear = new Date(offeringData.dateOfLaunch).getUTCFullYear();
  if (!Number.isFinite(launchYear)) return null;

  return `${launchYear} Round`;
};

/**
 * Generates a unique title for offering pages to prevent duplicates
 */
export const generateOfferingTitle = (
  offeringData: OfferingTitleData | null | undefined,
  options: {
    isUpdate?: boolean;
    updateData?: { title?: string } | null;
    seoData?: { metaTitle?: string } | null;
  } = {},
): string => {
  const { isUpdate = false, updateData, seoData } = options;

  if (seoData?.metaTitle) {
    return seoData.metaTitle;
  }
  if (isUpdate && updateData?.title) {
    return updateData.title;
  }

  const companyName = offeringData?.name || 'Unknown Company';

  if (companyName === 'Unknown Company') {
    return 'Unknown Company | StartEngine';
  }

  const identityTag = getOfferingIdentityTag(companyName, offeringData?.slug);
  if (identityTag) {
    return `Invest in ${companyName} (${identityTag}) | StartEngine`;
  }

  const closedRoundYearTag = getClosedRoundYearTag(offeringData);
  if (closedRoundYearTag) {
    return `Invest in ${companyName} (${closedRoundYearTag}) | StartEngine`;
  }

  return `Invest in ${companyName} | StartEngine`;
};

/**
 * Generates a unique title for explore pages with filters
 */
export const generateExploreTitle = (
  filters: {
    category?: string;
    search?: string;
    sortBy?: string;
    status?: string;
  } = {},
): string => {
  const { category, search, sortBy, status } = filters;

  if (search) {
    return `Search: ${search} | StartEngine`;
  }

  if (category) {
    const categoryTitle = category.replace(/([A-Z])/g, ' $1').trim();
    // Capitalize first letter of each word
    const capitalizedCategory = categoryTitle
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    return `${capitalizedCategory} Startups | StartEngine`;
  }

  if (status === 'funding') {
    return 'Currently Funding Startups | StartEngine';
  }

  if (status === 'closed') {
    return 'Closed Offerings | StartEngine';
  }

  if (sortBy === 'recent') {
    return 'Recently Added Startups | StartEngine';
  }

  return 'Explore Startups | StartEngine';
};

/**
 * Sanitizes and normalizes titles for SEO consistency
 */
export const sanitizeTitle = (title: string, maxLength: number = 60): string => {
  if (!title) return 'StartEngine';

  let sanitized = title
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s\-|()&.]/g, ''); // Allow & and .

  if (!sanitized.includes('StartEngine')) {
    sanitized += ' | StartEngine';
  }

  if (sanitized.length > maxLength) {
    sanitized = `${sanitized.substring(0, maxLength - 3)}...`;
  }

  return sanitized;
};

/**
 * Creates a unique title hash to prevent duplicates
 */
export const createTitleHash = (title: string, url: string): string => {
  try {
    const urlObj = new URL(url);
    const { pathname } = urlObj;
    return `${title}-${pathname}`;
  } catch {
    // If URL is invalid, just use the original URL string
    const urlPath = url.split('?')[0]; // Remove query parameters
    return `${title}-${urlPath}`;
  }
};

/**
 * Checks if a title is a duplicate based on URL and content
 */
export const isDuplicateTitle = (
  title: string,
  url: string,
  existingTitles: Map<string, string>,
): boolean => {
  const titleHash = createTitleHash(title, url);
  return existingTitles.has(titleHash);
};

/**
 * Generates a unique, SEO-friendly meta description based on page type and content
 */
export interface MetaDescriptionOptions {
  pageType: 'offering' | 'explore' | 'static' | 'notfound';
  companyName?: string;
  slug?: string;
  summary?: string;
  industry?: string;
  category?: string;
  search?: string;
  pageName?: string;
  defaultDescription?: string;
}

const OFFERING_DESCRIPTION_MAX_LENGTH = 155;
const MIN_CLAMPED_PITCH_LENGTH = 40;

const GENERIC_INDUSTRY_LABELS = new Set(['other', 'others', 'uncategorized', 'miscellaneous']);

const toPlainText = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const clampToSentence = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  const truncated = text.slice(0, maxLength - 3);
  const lastSpace = truncated.lastIndexOf(' ');
  const base = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;
  return `${base.replace(/[\s,;:.\-–—]+$/, '')}...`;
};

const getIndustryPhrase = (industry?: string): string => {
  const label = industry ? toPlainText(industry) : '';
  if (!label || GENERIC_INDUSTRY_LABELS.has(label.toLowerCase())) return '';
  const article = /^[aeiou]/i.test(label) ? 'an' : 'a';
  return `, ${article} ${label} company,`;
};

/**
 * Builds an offering description from the issuer's own pitch so each campaign
 * page gets a distinct snippet instead of the shared boilerplate, which Google
 * frequently rewrites or ignores across thousands of near-identical
 * /offering/* pages.
 *
 * When the pitch and the invitation together exceed the length budget, the
 * pitch is what gets clamped — never the invitation. The invitation carries the
 * company name, which is the only part of the snippet guaranteed to be unique,
 * while issuers routinely reuse one tagline across sibling campaigns
 * ("Buy Shares in Fine Wine" on every StartEngine Collectibles wine offering).
 * Dropping the invitation collapsed every long-named campaign sharing a tagline
 * onto byte-identical descriptions.
 */
const buildOfferingDescription = ({
  subject,
  summary,
  industry,
}: {
  subject: string;
  summary?: string;
  industry?: string;
}): string => {
  const industryPhrase = getIndustryPhrase(industry);
  const invitation = `Invest in ${subject}${industryPhrase} on StartEngine.`;
  const pitch = summary ? toPlainText(summary) : '';

  if (!pitch) {
    return clampToSentence(
      `${invitation} Learn about the founders, read reviews, and join thousands of investors.`,
      OFFERING_DESCRIPTION_MAX_LENGTH,
    );
  }

  const lead = /[.!?]$/.test(pitch) ? pitch : `${pitch}.`;
  const withPitch = `${lead} ${invitation}`;

  if (withPitch.length <= OFFERING_DESCRIPTION_MAX_LENGTH) return withPitch;

  const roomForLead = OFFERING_DESCRIPTION_MAX_LENGTH - invitation.length - 1;
  if (roomForLead >= MIN_CLAMPED_PITCH_LENGTH) {
    return `${clampToSentence(lead, roomForLead)} ${invitation}`;
  }

  return clampToSentence(invitation, OFFERING_DESCRIPTION_MAX_LENGTH);
};

export const generateMetaDescription = (options: MetaDescriptionOptions): string => {
  const {
    pageType,
    companyName,
    slug,
    summary,
    industry,
    category,
    search,
    pageName,
    defaultDescription = 'Invest online in startups you love. StartEngine gives everyday people the opportunity to invest and own shares in startups and early-growth companies.',
  } = options;

  if (pageType === 'offering' && companyName) {
    const identityTag = getOfferingIdentityTag(companyName, slug);
    const subject = identityTag ? `${companyName} (${identityTag})` : companyName;
    return buildOfferingDescription({ subject, summary, industry });
  }

  if (pageType === 'explore') {
    const clamp = (s: string) => (s.length > 155 ? `${s.substring(0, 152)}...` : s);
    if (category) {
      const categoryTitle = category.replace(/([A-Z])/g, ' $1').trim();
      const capitalizedCategory = categoryTitle
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return clamp(
        `Browse ${capitalizedCategory} startups currently raising capital on StartEngine. Invest in early-stage companies with equity crowdfunding starting at $100.`,
      );
    }
    if (search) {
      return clamp(
        `Search results for "${search}" on StartEngine. Find startups and private companies raising capital through equity crowdfunding.`,
      );
    }
    return clamp(
      'Browse startups and private companies currently raising capital on StartEngine. Invest in early-stage companies through equity crowdfunding starting at $100.',
    );
  }

  if (pageType === 'static' && pageName) {
    return `${pageName} page on StartEngine.`;
  }

  if (pageType === 'notfound') {
    return 'Page not found. Invest online in startups you love on StartEngine.';
  }

  return defaultDescription;
};
